import type { Fee, SpecialOffer } from '@vibesites/schema';
import type { IsoDate, Range } from './model';

const FALSE_SENTINEL = 'false';
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Reads the "false" sentinel as empty. */
export function text(value: unknown): string | null {
  if (value == null || value === false) return null;
  const raw = String(value).trim();
  if (!raw || raw.toLowerCase() === FALSE_SENTINEL) return null;
  return raw;
}

/** "1,022", "1239.00", and 2960 read as numbers above 0. "false", "", 0, and null read as null. */
export function amount(value: unknown): number | null {
  const n = number(value);
  if (n == null || n <= 0) return null;
  return n;
}

/** "0" reads as 0. "false" and "" read as null. */
export function count(value: unknown): number | null {
  const n = number(value);
  if (n == null || n < 0) return null;
  return n;
}

export function isoDate(value: unknown): IsoDate | null {
  const raw = text(value);
  if (!raw) return null;
  const match = ISO_DATE.exec(raw.slice(0, 10));
  if (!match) return null;
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${match[1]}-${match[2]}-${match[3]}` as IsoDate;
}

/** A missing max reads as min. */
export function span(min: number | null, max: number | null): Range | null {
  const lo = min ?? max;
  if (lo == null || lo <= 0) return null;
  const hi = max ?? lo;
  if (hi <= 0) return { min: lo, max: lo };
  return hi >= lo ? { min: lo, max: hi } : { min: hi, max: lo };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** "Dec 10". Passing `today` adds the year when it differs: "Apr 7, 2027". */
export function formatDay(iso: IsoDate, today?: IsoDate): string {
  const month = Number(iso.slice(5, 7));
  const day = Number(iso.slice(8, 10));
  const label = `${MONTHS[month - 1]} ${day}`;
  return today && iso.slice(0, 4) !== today.slice(0, 4) ? `${label}, ${iso.slice(0, 4)}` : label;
}

const WHOLE = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const CENTS = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function formatDollars(value: number): string {
  const cents = Math.round(value * 100);
  return cents % 100 === 0 ? WHOLE.format(value) : CENTS.format(value);
}

export function formatRange(range: Range): string {
  if (range.min === range.max) return formatDollars(range.min);
  return `${formatDollars(range.min)} to ${formatDollars(range.max)}`;
}

const WHOLE_NUMBER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const FRACTION_NUMBER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

export function formatNumber(value: number): string {
  return Number.isInteger(value) ? WHOLE_NUMBER.format(value) : FRACTION_NUMBER.format(value);
}

export function formatNumberRange(range: Range): string {
  if (range.min === range.max) return formatNumber(range.min);
  return `${formatNumber(range.min)} to ${formatNumber(range.max)}`;
}

function number(value: unknown): number | null {
  if (value == null || value === false || value === true) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const raw = String(value).trim().replace(/,/g, '');
  if (!raw || raw.toLowerCase() === FALSE_SENTINEL) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

type FeeAmountKind = 'fixed' | 'range' | 'text' | 'percentage' | 'other';

function feeAmountKind(fee: Fee): FeeAmountKind {
  const raw = text(fee.amount_type)?.toLowerCase();
  if (raw == null || raw === 'fixed') return 'fixed';
  if (raw === 'range' || raw === 'text' || raw === 'percentage') return raw;
  return 'other';
}

/** The amount alone: "$75 to $100", "Usage-Based (Utilities)", "10%". Null when the feed sends none. */
export function formatFeeAmount(fee: Fee): string | null {
  const kind = feeAmountKind(fee);
  switch (kind) {
    case 'text':
      return text(fee.amount_raw) ?? dollar(amount(fee.amount));
    case 'percentage':
      return text(fee.amount_raw) ?? percent(amount(fee.amount));
    case 'fixed':
    case 'range': {
      const range = span(amount(fee.amount), amount(fee.amount_max));
      return range ? formatRange(range) : text(fee.amount_raw);
    }
    case 'other':
      return text(fee.amount_raw);
    default: {
      const unhandled: never = kind;
      return unhandled;
    }
  }
}

function dollar(value: number | null): string | null {
  return value == null ? null : formatDollars(value);
}

function percent(value: number | null): string | null {
  return value == null ? null : `${formatNumber(value)}%`;
}

/** The amount for `fee`, then the feed's own timing, then "refundable". The fee name stays on `fee.name`. */
export function formatFee(fee: Fee): string {
  const timing = text(fee.timing);
  const parts = [formatFeeAmount(fee), timing && `(${timing})`];
  return [parts.filter(Boolean).join(' '), fee.is_refundable === true && 'refundable'].filter(Boolean).join(', ');
}

export type TourEmbed =
  /** An iframe src that plays in place. `provider` is the host, for "Loads from my.matterport.com". */
  | { readonly kind: '3d' | 'video'; readonly src: string; readonly provider: string }
  /** A host with no known embed. Open it in a new tab. */
  | { readonly kind: 'link'; readonly provider: string };

/** Matterport, Vimeo, and YouTube play in place. Any other host is a link. */
export function tourEmbed(href: string): TourEmbed {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return { kind: 'link', provider: href };
  }
  const provider = url.hostname.replace(/^www\./, '');
  if (provider.endsWith('matterport.com')) {
    const model = url.searchParams.get('m');
    if (model) return { kind: '3d', src: `https://my.matterport.com/show/?m=${encodeURIComponent(model)}&play=1`, provider };
  }
  if (provider === 'vimeo.com' || provider === 'player.vimeo.com') {
    const [, id, unlisted] = url.pathname.match(/(\d+)(?:\/([\da-f]+))?/) ?? [];
    const hash = url.searchParams.get('h') ?? unlisted;
    if (id) return { kind: 'video', src: `https://player.vimeo.com/video/${id}?autoplay=1${hash ? `&h=${encodeURIComponent(hash)}` : ''}`, provider };
  }
  if (provider === 'youtu.be' || provider.endsWith('youtube.com')) {
    const id = provider === 'youtu.be'
      ? url.pathname.slice(1)
      : url.searchParams.get('v') ?? url.pathname.match(/\/(?:embed|shorts|live)\/([\w-]+)/)?.[1];
    if (id) return { kind: 'video', src: `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1`, provider };
  }
  return { kind: 'link', provider };
}

/** The customer's title as sent, else the description. A start date after today adds "from {date}". */
export function formatSpecial(offer: SpecialOffer, today: IsoDate): string | null {
  const title = text(offer.title) ?? text(offer.description);
  if (!title) return null;
  const startsOn = isoDate(offer.start_date);
  return startsOn && startsOn > today ? `${title} from ${formatDay(startsOn, today)}` : title;
}

